/** Loading images and third-party scripts, with the timeouts a flaky network needs. */

export interface LoadImageOptions {
  crossOrigin?: string | null;
  timeout?: number;
}

export function loadImage(
  src: string,
  { crossOrigin = 'anonymous', timeout = 9000 }: LoadImageOptions = {},
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('no src'));
      return;
    }
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.referrerPolicy = 'no-referrer';
    const timer = setTimeout(() => {
      img.src = '';
      reject(new Error('image timeout'));
    }, timeout);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`image failed: ${src}`));
    };
    img.decoding = 'async';
    img.src = src;
  });
}

export interface LooseImage {
  img: HTMLImageElement;
  /** False when the image had to be loaded without CORS, so its pixels are unreadable. */
  cors: boolean;
}

/** Load an image, retrying once without CORS so it can still be displayed. */
export async function loadImageLoose(src: string): Promise<LooseImage | null> {
  try {
    return { img: await loadImage(src), cors: true };
  } catch {
    /* fall through to the no-CORS attempt */
  }
  try {
    return { img: await loadImage(src, { crossOrigin: null }), cors: false };
  } catch {
    return null;
  }
}

export function loadScript(src: string, id?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (id && document.getElementById(id)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    if (id) s.id = id;
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      resolve();
    };
    s.onerror = () => {
      reject(new Error(`script failed: ${src}`));
    };
    document.head.appendChild(s);
  });
}
